import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  ModalService,
  ScheduledPauseDisableConditionChecker,
  SurveySchedulePauseService,
  TemplateService,
} from '@purespectrum1/ui';
import {
  TableExpandableRowDirective,
  TableElementKey,
  TableBorder,
  TableStateEvent,
  TableComponent,
  TableConfig,
  TableStateFilterEvent,
} from '@purespectrum1/ui/table';
import {
  BehaviorSubject,
  from,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  shareReplay,
  startWith,
  switchMap,
  tap,
  toArray,
} from 'rxjs/operators';
import { BULK_STATUS_CHANGE_MODAL } from '../../constants/modal-constants';
import { notifyMessage } from '../../constants/notify-message';
import { BillingService } from '../../shared/services/billing-service/billing.service';
import { BulkInvoiceHelperService } from '../../shared/services/bulk-invoice/bulk-invoice-helper.service';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { CommonService } from '../../shared/services/common-service/common-service';
import { BulkSurveyStatusChangeModalComponent } from '../../shared/ui/bulk-survey-status-change-modal/bulk-survey-status-change-modal.component';
import { AuthService } from '../../auth/auth.service';
import { INVOICE_TYPE } from '../../constants/invoice-type';
import { STATUS_BUTTONS } from '../../constants/status-buttons';
import { SURVEY_STATUS } from '../../constants/survey-status';
import {
  SurveyListing,
  SurveyUpdate,
} from '../../shared/services/buyer-api/survey.interface';
import { SurveyStatus } from '../../utils/survey-status';
import { ChangeSurveyStatusModalComponent } from '../change-survey-status-modal/change-survey-status-modal.component';
import {
  Constants,
  FILTERING_LIMIT,
  OPERATOR_FILTERING_LIMIT,
} from '../dashboard-constants';
import { DashboardPreferenceModalComponent } from '../dashboard-preference-modal/dashboard-preference-modal.component';
import { DashboardPreferenceState } from '../domain/dashboard-preference';
import { DashboardTab } from '../domain/dashboard-tab';
import { TCCreateAction } from '../types';
import { SelectedWidget, Widget } from '../widget/widget';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { SurveyTableFilterDelegate } from './domain/survey-table-filter';
import { SurveysLength } from './domain/surveys-length';
import { EachCountry } from '../../shared/interfaces/common-service.interface';
import { SurveyRouterLinkPipe } from '../pipes/survey-router-link.pipe';
import { Buyer } from '../../operator-settings/operator-settings.interface';
import { Pagination } from '../../shared/types/pagination';
import { CompanyInfo } from '../../shared/interfaces/company.interface';
import { CompanyService } from '../../shared/services/company/company.service';
import {
  DashboardStateService,
  EMPTY_DASHBOARD_STATE,
} from '../services/dashboard-state.service';
import { TreeDropdownOption } from '@purespectrum1/ui/marketplace/create-surveys/shared/interfaces/tree-dropdown-interface';
import { TemplateListResponse } from '@purespectrum1/ui/marketplace/create-surveys/layout/template-modal/template-modal.interface';
import { DashboardSelectedSurveys } from '../domain/dasboard-survey-selected';
import { BuyerSurveyResponse } from '@purespectrum1/ui/marketplace/shared/interfaces/survey.interface';
import { SurveyTableSortDelegate } from './domain/survey-table-sort';
import { FiltersFlagMapper, SearchFlags } from './domain/filters-flag-mapper';

type DeleteAction = {
  id: number;
  status: number;
};

@Component({
  selector: 'ps-survey-table',
  templateUrl: './survey-table.component.html',
  styleUrls: ['./survey-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyTableComponent implements OnInit, OnDestroy {
  @Input()
  public preferences!: DashboardPreferenceState;

  @Input()
  set tab(tab: DashboardTab) {
    this._tabCommand$.next(tab);
  }

  @Input()
  public surveys: SurveyListing[] = [];

  @Input()
  public set selected(surveys: SurveyListing[]) {
    this.selectedKeys = surveys.map((survey) => survey.surveyId);
  }

  @Input()
  public countries: EachCountry[] = [];

  @Input()
  public isDasaboard: boolean = false;

  @Input()
  public isMultiCountryWidget: boolean = false;

  @Input()
  public set enableBackEndSortFilter(value: boolean) {
    this.config$.next({
      internalOperations: !value,
    });
  }

  @Input()
  public resetScroll: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public companyChange = new EventEmitter<Buyer>();

  @Output()
  public paginate = new EventEmitter();

  @Output()
  public clone = new EventEmitter<number>();

  @Output()
  public delete = new EventEmitter<number[]>();

  @Output()
  public tc = new EventEmitter<TCCreateAction>();

  @Output()
  public downloadReport = new EventEmitter<number>();

  @Output()
  public selectedChange = new EventEmitter<SurveyListing[]>();

  @ViewChild(TableExpandableRowDirective)
  public expandable!: TableExpandableRowDirective;

  @HostBinding('style.--custom-table-header-padding')
  public get tableHeaderPadding() {
    return this.preferences.enableBulkEditAndSorting ? '3px' : '8px';
  }

  @ViewChild(TableComponent)
  public table!: TableComponent<Record<string, unknown>>;

  public get supplier(): boolean {
    return this.preferences.user === 'supplier';
  }

  public get length() {
    return new SurveysLength(this.surveys).calculate();
  }

  public get border(): TableBorder {
    return 'bottom';
  }

  get isCompanySelectionAllowed() {
    return (
      this._auth.userType === 'operator' &&
      !this._auth.isLoggedInAsServiceBuyer()
    );
  }

  public SURVEY_LISTING_TABLE_HEADERS = Constants.SURVEY_LISTING_TABLE_HEADERS;
  public RECONTACT_SURVEY_SAMPLE_TYPE = Constants.RECONTACT_SURVEY_SAMPLE_TYPE;
  public RECONCILIATION = Constants.RECONCILIATION;
  public SURVEY_STATUS = SurveyStatus;
  public statusButtons = STATUS_BUTTONS;
  public surveyStatus: { value: number; name: string }[] = [];
  public currentCompany?: CompanyInfo;
  public companies: CompanyInfo[] = [];
  public companies$!: Observable<CompanyInfo[]>;
  public state: TableStateEvent = EMPTY_DASHBOARD_STATE;
  public templates$!: Observable<TemplateListResponse[]>;
  public preventCheckbox: boolean = false;
  public readOnlyFlags: SearchFlags = new FiltersFlagMapper(
    EMPTY_DASHBOARD_STATE.filter
  ).readonlyFlags();

  public selectedWidget = SelectedWidget.empty();
  public managing = true;
  public edit: boolean = false;
  public selectedSurveys: TableElementKey<string>[] = [];
  public selection: boolean = false;
  public filter = new SurveyTableFilterDelegate();
  public sort = new SurveyTableSortDelegate();
  public selectedKeys: number[] = [];
  public tab$!: Observable<DashboardTab>;
  public isPacingTooltipLeft: boolean = false;
  public initialConfig: TableConfig = {
    internalOperations: true,
  };

  public templateOption: TreeDropdownOption = {
    uniqueKey: 'id',
    displayKey: 'name',
    navigationKey: 'templates',
    removeItem: false,
    icon: 'fa fa-file-text-o',
    noContentMsg: 'No Template created yet',
    mcIconIdentifier: 'is_mc_template',
  };

  public templateFolderOption: TreeDropdownOption = {
    uniqueKey: 'id',
    displayKey: 'name',
    navigationKey: 'subfolders',
    headerLabel: 'My Templates',
    removeItem: false,
    icon: 'fa fa-folder',
    noContentMsg: 'No Template created yet', // Even though it is template-folder content, this message is for ease of user
    mcIconIdentifier: 'is_mc_template',
  };

  public readonly config$ = new BehaviorSubject<TableConfig>(
    this.initialConfig
  );

  private _deleteCommand$ = new Subject<DeleteAction>();
  private _tabCommand$ = new BehaviorSubject<DashboardTab>(
    DashboardTab.from(Constants.SURVEY_TABS[0])
  );
  private _subscriptions = new Subscription();
  private _tab!: DashboardTab;
  private _isMasterMultiBuyer: boolean = false;
  private _searchCommand$ = new Subject<string>();
  private _allTemplates$: Observable<TemplateListResponse[]> =
    this._templateService.getUpdatedTemplateList().pipe(shareReplay(1));
  public healtcheckEnabled: boolean = false;

  constructor(
    // TODO: Remove service injetion.
    // This component should be presentation only
    private _buyerApiService: BuyerApiService,
    private _billingService: BillingService,
    private _commonService: CommonService,
    private _companyService: CompanyService,
    private _bulkInvoiceHelperService: BulkInvoiceHelperService,
    private _templateService: TemplateService,
    private _auth: AuthService,
    private _toastr: ToasterService,
    // End
    private _dashboardStateService: DashboardStateService,
    private readonly _route: Router,
    private readonly _modal: ModalService,
    private readonly _surveySchedulePauseService: SurveySchedulePauseService
  ) {}

  public changeBar = () => {
    return;
  };

  public ngOnInit(): void {
    const stateSubscription = this._dashboardStateService.state$
      .pipe(
        startWith(this._dashboardStateService.lastState),
        tap((state) => {
          this.state = this._manageState(state);
        })
      )
      .subscribe();

    this._subscriptions.add(stateSubscription);

    this.tab$ = merge(
      this._auth.loggedInAsServiceBuyer$.pipe(map(() => this._tab)),
      this._tabCommand$.pipe(
        tap((tab) => {
          this._tab = tab;
        })
      )
    ).pipe(
      debounceTime(400),
      tap(() => {
        this.surveyStatus = this._surveyStatus();
      })
    );

    const _delete = this._deleteCommand$
      .pipe(
        switchMap((action) =>
          this._modal
            .open(ChangeSurveyStatusModalComponent, {
              data: {
                surveyId: action.id,
                status: action.status,
                toDelete: true,
              },
              width: '450px',
              height: '18.75rem',
            })
            .onClose$.pipe(
              filter((msg) => msg === Constants.MODAL_MESSAGE.DELETE_SURVEY),
              mapTo(action)
            )
        )
      )
      .subscribe((action) => this.delete.emit([action.id]));

    this.initiateCompanySearch();
    this._initializeTemplates();

    this.healtcheckEnabled = this._auth.buyerConfig!.enableHealthMetrics;
    this._isMasterMultiBuyer = this._auth.isLoggedInAsMasterMultiBuyer();
    this._subscriptions.add(_delete);
  }

  private _initializeTemplates(): void {
    this.templates$ = this._allTemplates$.pipe(
      switchMap((templates) => {
        const filtered = this.state.filter.find((f) => f.field === 'template');

        return from(templates).pipe(
          map((template) => {
            template.templates = template.templates.map((value) => ({
              ...value,
              isChecked: filtered?.values.includes(value.name),
            }));

            return template;
          }),
          toArray()
        );
      })
    );
  }

  onHoverPacing(surveyId: number | null) {
    if (!surveyId) {
      return;
    }

    const element = document.querySelector(`.tooltip-pacing-${surveyId}`);
    if (!(element instanceof HTMLElement)) {
      return;
    }

    const tableElement = this.table?.container?.nativeElement;

    const tableRect = tableElement.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    const spaceOnRight =
      tableRect.right - elementRect.right - elementRect.width;
    this.isPacingTooltipLeft = spaceOnRight < 0;
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public getDay(event: any, survey: any) {
    if (event.day >= 0) {
      survey.showDay = event;
    }
    survey.lineChartActive = !survey.lineChartActive;
  }

  public getHour(event: any, survey: any) {
    survey.lineChartActive = !survey.lineChartActive;
  }

  // Note: Only call this method when `enableBackEndSortFilter` is enabled.
  private _handleFiltersSearchState(
    indexes: { searchIndex: number; customIndex: number },
    filters: TableStateFilterEvent[]
  ) {
    const indexToDelete =
      indexes.searchIndex < indexes.customIndex
        ? indexes.searchIndex
        : indexes.customIndex;
    if (indexToDelete > -1) {
      filters.splice(indexToDelete, 1);
    }
    return filters;
  }

  /**
   * Only call _handleFiltersWithSearch, when `enableBackEndSortFilter` is Enabled.
   * @param filters : Filters with search and selection options, those are applied on survey table, Going to managed with their state, based on the priority order of last applied filter (selection or search).
   * @returns {Array<TableStateFilterEvent>}
   */
  private _handleFiltersWithSearch(filters: TableStateFilterEvent[]) {
    const {
      bpmFilters,
      pmFilters,
      billingFilters,
    }: {
      bpmFilters: { searchIndex: number; customIndex: number };
      pmFilters: { searchIndex: number; customIndex: number };
      billingFilters: { searchIndex: number; customIndex: number };
    } = filters.reduce(
      (acc, curr, index) => {
        if (
          curr.field === this.SURVEY_LISTING_TABLE_HEADERS.BUYER_PROJECT_MANAGER
        ) {
          if (curr.type === 'search') {
            acc.bpmFilters.searchIndex = index;
          } else {
            acc.bpmFilters.customIndex = index;
          }
        } else if (
          curr.field ===
          this.SURVEY_LISTING_TABLE_HEADERS.OPERATOR_PROJECT_MANAGER
        ) {
          if (curr.type === 'search') {
            acc.pmFilters.searchIndex = index;
          } else {
            acc.pmFilters.customIndex = index;
          }
        } else if (
          curr.field === this.SURVEY_LISTING_TABLE_HEADERS.BILLING_NUMBER
        ) {
          if (curr.type === 'search') {
            acc.billingFilters.searchIndex = index;
          } else {
            acc.billingFilters.customIndex = index;
          }
        }
        return acc;
      },
      {
        bpmFilters: { searchIndex: -1, customIndex: -1 },
        pmFilters: { searchIndex: -1, customIndex: -1 },
        billingFilters: { searchIndex: -1, customIndex: -1 },
      }
    );

    filters = this._handleFiltersSearchState(bpmFilters, filters);
    filters = this._handleFiltersSearchState(pmFilters, filters);
    filters = this._handleFiltersSearchState(billingFilters, filters);

    return filters;
  }

  /**
   * Applies backend-specific filtering rules to the table state.
   * Ensures only valid filters are passed to the backend and enforces filter limits.
   */
  private _backendFilteringState(state: TableStateEvent): TableStateEvent {
    // If backend filtering is not enabled and not an operator, return the state unchanged.
    if (
      !this._auth.buyerConfig?.enableBackEndSortFilter &&
      !this.isCompanySelectionAllowed
    ) {
      return state;
    }

    // Keep only filters with non-empty, truthy values.
    let validFilters = state.filter.filter(
      (f) =>
        Array.isArray(f.values) &&
        f.values.length > 0 &&
        f.values.every((v) => Boolean(v))
    );

    validFilters = this._handleFiltersWithSearch(validFilters);

    const filteringLimit = this.isCompanySelectionAllowed
      ? OPERATOR_FILTERING_LIMIT
      : FILTERING_LIMIT;
    this.preventCheckbox = validFilters.length >= filteringLimit;

    if (validFilters.length > filteringLimit) {
      this._toastr.error(
        notifyMessage.errorMessage.BACKEND_FILTERING_LIMIT.replace(
          '<FILTER_LIMIT>',
          filteringLimit.toString()
        )
      );
    }

    const limitedFilters = validFilters.slice(0, filteringLimit);
    const removedFilters = validFilters.slice(filteringLimit);

    // if template's is available in removed / invalid filter rules, need to reset the templates selection
    if (removedFilters.findIndex((f) => f.field === 'template') > -1) {
      this._initializeTemplates();
    }

    this.readOnlyFlags = new FiltersFlagMapper(limitedFilters).readonlyFlags(
      this.preventCheckbox
    );

    return {
      ...state,
      filter: limitedFilters,
    };
  }

  // method used for managing table state before updating the dashboard state service
  private _manageState(state: TableStateEvent): TableStateEvent {
    return this._backendFilteringState(state);
  }

  public onTableStateChange(state: TableStateEvent): void {
    const updatedState = this._manageState(state);
    this._dashboardStateService.update(updatedState);

    if (this.isCompanySelectionAllowed) {
      if (
        this.currentCompany &&
        !updatedState.filter.find(
          (data) =>
            data.field === this.SURVEY_LISTING_TABLE_HEADERS.BUYER_ACCOUNT &&
            data.type === 'option'
        )
      ) {
        this.companyChange.emit();
        this.companies = [];
        this.currentCompany = undefined;
        this._dashboardStateService.company = undefined;
        this._dashboardStateService.selected = DashboardSelectedSurveys.empty();
        this._searchCommand$.next();

        // Clear the filter and sort state when the company is cleared
        this._dashboardStateService.clearState();
        return;
      }

      if (
        updatedState.searchText &&
        updatedState.searchField ===
          this.SURVEY_LISTING_TABLE_HEADERS.BUYER_ACCOUNT
      ) {
        this._searchCommand$.next(updatedState.searchText);
        return;
      }

      const company = this.getBuyerCompany(updatedState);
      if (company && company?.id !== this.currentCompany?.id) {
        this.currentCompany = company;
        this.companyChange.emit(this.currentCompany);
        return;
      }
    }
  }

  public onScrolled(): void {
    this.paginate.emit();
  }

  public initiateCompanySearch() {
    const search$ = this._searchCommand$.pipe(
      debounceTime(1000),
      distinctUntilChanged()
    );

    this.companies = this._dashboardStateService.company
      ? [this._dashboardStateService.company]
      : [];

    this.currentCompany = this._dashboardStateService.company;

    this.companies$ = search$.pipe(
      switchMap((search) =>
        this._companyService
          .getCompaniesPage(Pagination.first(), search, 'buyer')
          .pipe(
            map((response) => {
              return !!search ? response : { companies: [] };
            }),
            catchError((error) => {
              this._toastr.error(error.error.msg);
              return of({ count: 0, companies: [] });
            })
          )
      ),
      map((response) => {
        this.companies = response.companies;
        return response.companies;
      }),
      startWith(this.companies)
    );
  }

  public getBuyerCompany(state: TableStateEvent): CompanyInfo | undefined {
    const buyerFilter = state.filter.filter(
      (b) =>
        b.field === this.SURVEY_LISTING_TABLE_HEADERS.BUYER_ACCOUNT &&
        b?.values?.[0]?.length
    );
    const companyName = buyerFilter?.[buyerFilter.length - 1]?.values?.[0];
    return this.companies.find((c) => c.name === companyName);
  }

  public checkUserLevelAccess() {
    return (
      (this.preferences.user === 'operator' &&
        !this.preferences.serviceOperator) ||
      this.preferences.user === 'supplier' ||
      this._isMasterMultiBuyer
    );
  }

  public checkDisableSurveyStatus(survey: SurveyListing) {
    return (
      [SURVEY_STATUS.DRAFT, SURVEY_STATUS.INVOICE].indexOf(survey.status) >
        -1 ||
      this.preferences.user === 'supplier' ||
      this._isMasterMultiBuyer ||
      (survey.status === SURVEY_STATUS.CLOSED &&
        survey?.company?.generate_invoice === INVOICE_TYPE.MONTHLY)
    );
  }

  public openDashboardPreferenceModal() {
    this._modal.open(DashboardPreferenceModalComponent, {
      height: '80%',
      width: '80%',
    });
  }

  public onWidgetPressed(widget: Widget, survey: SurveyListing, index: number) {
    if (
      !this.selectedWidget.for(survey) ||
      this.selectedWidget.equals(widget)
    ) {
      this.expandable.expand(index);
    }

    this.selectedWidget = this.selectedWidget.select(widget, survey);
  }

  public onDetails(id: number, status: number, samplesType: number) {
    if (status === SurveyStatus.Draft) {
      if (samplesType === Constants.RECONTACT_SURVEY_SAMPLE_TYPE) {
        this._route.navigate(['recontact-survey', id]);
      } else {
        this._route.navigate([`/launch-survey/${id}`]);
      }
    } else {
      this._route.navigate([`/dashboard/${id}`]);
    }
  }

  public onOpenTrafficChannelManage(): void {
    this.managing = false;
  }

  public onCloseManageMenu(): void {
    this.managing = true;
  }

  public onClone(id: number): void {
    this.clone.emit(id);
  }

  public onDelete(id: number, status: number): void {
    this._deleteCommand$.next({ id, status });
  }

  public onCreateTC(type: string, request: string, id: number): void {
    this.tc.emit({
      type,
      request,
      id,
    });
  }

  public onDownloadReport(id: number): void {
    this.downloadReport.emit(id);
  }

  public onSurveysSelected(ids: TableElementKey<string>[]) {
    const selection = this.surveys.filter((survey) =>
      ids.some((id) => id === survey.surveyId)
    );

    this.selectedChange.emit(selection);
  }

  public onTableCellClicked(survey: SurveyListing): void {
    const route = new SurveyRouterLinkPipe().transform(survey);
    const buyerId = survey.buyerAccount;

    this._route.navigate(route, {
      state: {
        buyerCompanyId: buyerId,
      },
    });
  }

  public onSelectedTemplates(templates: { id: number; name: string }[]): void {
    const previousFilters = this.state.filter.filter(
      (f) => f.field !== 'template'
    );

    let state = {
      ...this.state,
      filter: [
        ...previousFilters,
        {
          field: 'template',
          type: 'option' as const,
          values: templates.map((template) => template.name.toString()),
          customValues: templates.map((template) => template.id.toString()),
        },
      ],
    };

    state = this._manageState(state);
    this._dashboardStateService.update(state);
  }

  // TODO: This should be refacted and extracted from this component when we integrate
  // invoice status changes in the new bulk edit dashboard
  async onChangeStatus(newStatus: string, survey: SurveyListing) {
    const {
      surveyId,
      billing_id,
      survey_pause_threshold,
      is_survey_pause_threshold_triggered,
      aCpi,
      currencySymbol,
    } = survey;

    const scheduledPauseConfirmed =
      await this._disableScheduledPauseConfirmation(
        survey,
        newStatus
      ).toPromise();

    if (!scheduledPauseConfirmed) {
      return;
    }

    const isBillingConfirmationRequired = this._isBillingConfirmationRequired(
      newStatus,
      billing_id
    );
    if (isBillingConfirmationRequired) {
      const shouldProceed = await this._billingService.openBillingNumberModal();
      if (!shouldProceed) {
        return;
      }
    }
    const [isProceed, isBulkInvoicingCase, surveysToClose, surveysToInvoice] =
      await this._checkIfItisBulkInvoicingCase(surveyId, newStatus, billing_id);
    if (!isProceed) {
      return;
    }
    if (!isBulkInvoicingCase) {
      return this.openSingleStatusUpdateModal(
        surveyId,
        newStatus,
        survey_pause_threshold!,
        is_survey_pause_threshold_triggered!,
        aCpi!,
        currencySymbol
      );
    }
    return this._updateSurveyStatuses(
      surveyId,
      surveysToClose,
      surveysToInvoice
    );
  }

  private _isBillingConfirmationRequired(status: string, billingId: string) {
    const numberedStatus = Number(this._commonService.getSurveyStatus(status));
    return !!(
      !this._auth.buyerConfig?.enableMandatoryBillingNumber &&
      !billingId &&
      numberedStatus === SurveyStatus.Invoice
    );
  }

  private async _checkIfItisBulkInvoicingCase(
    currentSurveyId: number,
    status: string,
    billingId: string
  ): Promise<[boolean, boolean, SurveyUpdate[], SurveyUpdate[]]> {
    const numberedStatus = Number(this._commonService.getSurveyStatus(status));
    if (numberedStatus !== SurveyStatus.Invoice) {
      return [true, false, [], []];
    }
    return this._checkAndShowModalIfSurveysWithSamePo(
      currentSurveyId,
      numberedStatus,
      billingId
    );
  }

  private async _checkAndShowModalIfSurveysWithSamePo(
    currentSurveyId: number,
    status: SurveyStatus,
    billingId: string
  ) {
    let notInvoicedSurveysWithSamePo =
      await this._fetchLaunchedAndNotInvoicedSurveysWithSamePo(billingId);
    notInvoicedSurveysWithSamePo = notInvoicedSurveysWithSamePo.filter(
      (s) => Number(s.ps_survey_id) !== Number(currentSurveyId)
    );
    if (!notInvoicedSurveysWithSamePo.length) {
      return this._bulkInvoiceHelperService.decideSurveysToChangeStatusOf(
        status,
        BULK_STATUS_CHANGE_MODAL.NO_BULK_INVOICING_ACTION,
        [],
        [currentSurveyId]
      );
    }
    const action = await this._showModalIfInvoicedSurveysWithSamePo(
      notInvoicedSurveysWithSamePo
    );
    return this._bulkInvoiceHelperService.decideSurveysToChangeStatusOf(
      status,
      action,
      notInvoicedSurveysWithSamePo,
      [currentSurveyId]
    );
  }

  private async _fetchLaunchedAndNotInvoicedSurveysWithSamePo(
    billingId: string
  ) {
    if (!billingId) {
      return [];
    }
    return this._buyerApiService
      .getLaunchedAndNotInvoicedSurveysWithSamePo(billingId)
      .toPromise();
  }

  private async _showModalIfInvoicedSurveysWithSamePo(
    notInvoicedSurveysWithSamePo: BuyerSurveyResponse[]
  ) {
    const notInvoicedSurveyIdsWithSamePo =
      this._bulkInvoiceHelperService.fetchNotInvoicedSortedSurveyIdsWithSamePo(
        notInvoicedSurveysWithSamePo
      );
    return this._modal
      .open(BulkSurveyStatusChangeModalComponent, {
        data: {
          thisAndAllBtnText:
            BULK_STATUS_CHANGE_MODAL.BUTTON.THIS_AND_ALL_LISTED.TEXT
              .SURVEY_DETAIL,
          onlyThisBtnText:
            BULK_STATUS_CHANGE_MODAL.BUTTON.ONLY_THIS.TEXT.SURVEY_DETAIL,
          surveyIds: notInvoicedSurveyIdsWithSamePo.join(', '),
        },
        width: '60%',
      })
      .onClose$.toPromise();
  }

  private async _updateSurveyStatuses(
    currentSurveyId: number,
    surveysToClose: SurveyUpdate[],
    surveysToInvoice: SurveyUpdate[]
  ) {
    await this._buyerApiService
      .updateSurveyStatusesInBulk(surveysToClose)
      .toPromise();
    this._buyerApiService
      .updateSurveyStatusesInBulk(surveysToInvoice)
      .subscribe(
        (data) => {
          const surveyUpdateData = Object.keys(data).map((surveyId) => {
            return {
              surveyId: Number(surveyId),
              status: SurveyStatus.Invoice,
            };
          });

          const ids = surveyUpdateData.map((survey) => survey.surveyId);
          this.delete.emit(ids);

          const isBulkInvoiced = Object.keys(data).length > 1;
          this._toastr.success(
            isBulkInvoiced
              ? notifyMessage.successMessage.SURVEY_DETAIL.BULK_SURVEYS_INVOICED
              : notifyMessage.successMessage.SURVEY_DETAIL.SINGLE_SURVEY_INVOICED.replace(
                  '<SURVEY_ID>',
                  String(currentSurveyId)
                )
          );
        },
        (error) => {
          this._toastr.error(error.error?.ps_api_response_message || error.msg);
        }
      );
  }

  public openSingleStatusUpdateModal(
    surveyId: number,
    status: string,
    survey_pause_threshold: number,
    is_survey_pause_threshold_triggered: boolean,
    currentCpi: number,
    currencySymbol: string
  ): void {
    const survey = this.surveys.find((s) => s.surveyId === surveyId);
    const isSvpProject = survey?.mc?.SVP && survey?.mc?.parent;

    const surveyUpdateData = {
      surveyId: surveyId,
      status: status,
      survey_pause_threshold,
      is_survey_pause_threshold_triggered,
      currentCpi,
      currencySymbol,
      isSvpProject,
    };
    const modalRef = this._modal.open(ChangeSurveyStatusModalComponent, {
      data: surveyUpdateData,
      width: '450px',
      height: '18.75rem',
    });
    modalRef.onClose$.subscribe((msg) => {
      if (msg === Constants.MODAL_MESSAGE.UPDATE_SURVEY_STATUS) {
        this.delete.emit([surveyUpdateData.surveyId]);
      }
    });
  }

  public _surveyStatus() {
    return Object.entries(SURVEY_STATUS)
      .map(([name, value]) => ({
        value,
        name,
      }))
      .filter(
        (status) =>
          status.name !== 'INVOICE' ||
          ['PO', 'PROJECT'].includes(
            this._auth.companyConfig.generateInvoice || ''
          )
      )
      .filter(
        (status) =>
          status.name !== 'DRAFT' ||
          this._auth.userType !== 'operator' ||
          this._auth.isLoggedInAsServiceBuyer()
      );
  }

  public isDashboardEnabled(survey: SurveyListing): boolean {
    if (survey?.mc?.SVP && survey?.mc?.parent) {
      return true;
    }

    if (
      survey?.mc?.SVP &&
      !survey?.mc?.parent &&
      this._auth.userType === 'operator'
    ) {
      return false;
    }

    return this.isMultiCountryWidget;
  }

  private _disableScheduledPauseConfirmation(
    survey: SurveyListing,
    newStatus: string
  ): Observable<boolean> {
    const showModal = new ScheduledPauseDisableConditionChecker(
      survey.status,
      newStatus,
      survey.schedule_pause
    ).check();
    if (showModal) {
      return this._surveySchedulePauseService
        .openDisableConfirmationModal([
          {
            id: survey.surveyId,
            schedule_pause: survey.schedule_pause,
            status: survey.status,
          },
        ])
        .pipe(
          tap((disabled) => {
            if (disabled) {
              survey.schedule_pause.enable = false;
            }
          })
        );
    }

    return of(true);
  }
}
